// Helpdesk → AI-voorstellen: weergavehulpen zonder React, zodat ze te testen zijn
// (helpdeskAi.test.js). De cijfers zelf komen van de server
// (backend/src/util/aiInzicht.js); hier staat alleen hoe ze getoond worden.

export const AI_DELEN = [
  { value: 'inzicht', label: 'Inzicht' },
  { value: 'voorstellen', label: 'Voorstellen' },
  { value: 'bijsturen', label: 'Bijsturen' },
  { value: 'kennis', label: 'Kennisbronnen' },
];
// Zelfde keuzes als de server aanvaardt (PERIODES in aiInzicht.js); 0 = alles.
export const AI_PERIODES = [
  { value: 30, label: '30 dagen' },
  { value: 90, label: '90 dagen' },
  { value: 365, label: '12 maanden' },
  { value: 0, label: 'Alles' },
];
export const AI_VARIANTEN = [
  { value: '', label: 'Alle' },
  { value: 'voorstel', label: 'AI voorstel' },
  { value: '360', label: 'AI 360°' },
];
export const AI_UITKOMST = {
  ongewijzigd: { label: 'Ongewijzigd verstuurd', kort: 'Ongewijzigd', pill: 'hd-pill-emerald' },
  aangepast: { label: 'Aangepast verstuurd', kort: 'Aangepast', pill: 'hd-pill-amber' },
  niet_gebruikt: { label: 'Niet gebruikt', kort: 'Niet gebruikt', pill: 'hd-pill-slate' },
};
export const AI_MAX_INSTRUCTIE = 600;

const getal = (v, d = 0) => new Intl.NumberFormat('nl-BE', { minimumFractionDigits: 0, maximumFractionDigits: d }).format(v);
const isGetal = (v) => v != null && v !== '' && Number.isFinite(Number(v));

export const aiGetal = (v) => (isGetal(v) ? getal(Number(v)) : '—');
export const aiPct = (v) => (isGetal(v) ? `${getal(Number(v), 1)} %` : '—');
// Duur in seconden met één decimaal; vanaf een minuut in minuten en seconden.
export const aiDuur = (ms) => {
  if (!isGetal(ms) || Number(ms) <= 0) return '—';
  const s = Number(ms) / 1000;
  if (s < 60) return `${getal(s, 1)} s`;
  return `${Math.floor(s / 60)} min ${String(Math.round(s % 60)).padStart(2, '0')} s`;
};
// Tokens: duizendtallen met "k", zodat een totaal van miljoenen leesbaar blijft.
export const aiTokens = (v) => {
  if (!isGetal(v)) return '—';
  const n = Number(v);
  if (n >= 1000000) return `${getal(n / 1000000, 1)} mln`;
  if (n >= 10000) return `${getal(n / 1000, 0)}k`;
  return getal(n);
};

// Verschil met de vorige periode, voor onder een kengetal. `null` = er valt niets
// te vergelijken (geen vorige periode, of die was leeg én nu ook).
export function aiVerschil(nu, vorige) {
  if (!isGetal(nu) || !isGetal(vorige)) return null;
  const a = Number(nu);
  const b = Number(vorige);
  if (b === 0) return a === 0 ? null : { tekst: 'vorige periode: geen', richting: 'op' };
  const pct = ((a - b) / b) * 100;
  if (Math.abs(pct) < 0.5) return { tekst: 'gelijk aan vorige periode', richting: 'gelijk' };
  return { tekst: `${pct > 0 ? '+' : '−'}${getal(Math.abs(pct), 0)} % t.o.v. vorige periode`, richting: pct > 0 ? 'op' : 'neer' };
}
// Verschil tussen twee percentages, in procentpunten.
export function aiVerschilPunten(nu, vorige) {
  if (!isGetal(nu) || !isGetal(vorige)) return null;
  const d = Number(nu) - Number(vorige);
  if (Math.abs(d) < 0.05) return { tekst: 'gelijk aan vorige periode', richting: 'gelijk' };
  return { tekst: `${d > 0 ? '+' : '−'}${getal(Math.abs(d), 1)} procentpunt t.o.v. vorige periode`, richting: d > 0 ? 'op' : 'neer' };
}

const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
// Label onder een staaf: week = de maandag (DD/MM), maand = "sep '26".
export function aiPeriodeLabel(periode, eenheid) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(periode || ''));
  if (!m) return String(periode || '');
  return eenheid === 'maand' ? `${MAANDEN[Number(m[2]) - 1]} '${m[1].slice(2)}` : `${m[3]}/${m[2]}`;
}
// Volledige omschrijving voor de tooltip.
export function aiPeriodeTitel(periode, eenheid) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(periode || ''));
  if (!m) return String(periode || '');
  return eenheid === 'maand' ? `${MAANDEN[Number(m[2]) - 1]} ${m[1]}` : `Week van ${m[3]}/${m[2]}/${m[1]}`;
}

// Datum en uur in DD/MM/YYYY HH:mm, Belgische tijd.
const DATUM_TIJD = new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Brussels' });
const DATUM = new Intl.DateTimeFormat('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Brussels' });
const delen = (fmt, d) => Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
export function aiDatumTijd(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '—';
  const p = delen(DATUM_TIJD, d);
  return `${p.day}/${p.month}/${p.year} ${p.hour === '24' ? '00' : p.hour}:${p.minute}`;
}
export function aiDatum(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '—';
  const p = delen(DATUM, d);
  return `${p.day}/${p.month}/${p.year}`;
}

// Eerste letter groot: de namen van de soorten klantgegevens komen in kleine
// letters van de server (ze staan daar midden in een zin).
export const aiHoofdletter = (s) => { const t = String(s || ''); return t ? t.charAt(0).toLocaleUpperCase('nl-BE') + t.slice(1) : ''; };
// Soort bron zoals het model ze opgeeft.
export const aiBronSoort = (type) => (type === 'kennisbank' ? 'werkinstructie' : type === 'voorwaarden' ? 'algemene voorwaarden' : type === 'standaardtekst' ? 'standaardtekst' : String(type || ''));
export const aiVariantNaam = (v) => (v === '360' ? 'AI 360°' : 'AI voorstel');
export const aiKanaalNaam = (k) => (k === 'partner_internal' ? 'Intern bericht' : 'Mail naar klant');
export const aiBeoordelingNaam = (b) => (Number(b) === 1 ? 'Goed' : Number(b) === -1 ? 'Niet goed' : '');

// Querystring voor overzicht en lijst: alleen wat gekozen is.
export function aiQuery({ dagen = 30, label = '', variant = '', beoordeling = '', uitkomst = '', opmerking = false, leren = '' } = {}) {
  const q = new URLSearchParams();
  q.set('dagen', String(dagen));
  if (label) q.set('label', label);
  if (variant) q.set('variant', variant);
  if (beoordeling) q.set('beoordeling', beoordeling);
  if (uitkomst) q.set('uitkomst', uitkomst);
  if (opmerking) q.set('opmerking', '1');
  if (leren) q.set('leren', leren);
  return q.toString();
}

// Kolommen voor de Excel-export van een groepstabel (per label, per medewerker…).
export const aiGroepKolommen = (eersteKolom) => [
  { header: eersteKolom, get: (r) => r.naam },
  { header: 'Voorstellen', get: (r) => r.voorstellen },
  { header: 'Verstuurd', get: (r) => r.verstuurd },
  { header: 'Ongewijzigd verstuurd', get: (r) => r.ongewijzigd },
  { header: 'Aangepast verstuurd', get: (r) => r.aangepast },
  { header: 'Niet gebruikt', get: (r) => r.niet_gebruikt },
  { header: 'Goed beoordeeld', get: (r) => r.goed },
  { header: 'Niet goed beoordeeld', get: (r) => r.slecht },
  { header: 'Gemiddelde duur (s)', get: (r) => (isGetal(r.gem_duur_ms) ? Math.round(Number(r.gem_duur_ms) / 100) / 10 : '') },
  { header: 'Tokens in', get: (r) => r.tokens_in },
  { header: 'Tokens uit', get: (r) => r.tokens_uit },
];
export const AI_VOORSTEL_KOLOMMEN = [
  { header: 'Datum', get: (r) => aiDatumTijd(r.created_at) },
  { header: 'Ticket', get: (r) => r.ticket_ref || r.ticket_id || '' },
  { header: 'Onderwerp', get: (r) => r.onderwerp || '' },
  { header: 'Energielabel', get: (r) => r.energy_label || '' },
  { header: 'Variant', get: (r) => aiVariantNaam(r.variant) },
  { header: 'Kanaal', get: (r) => aiKanaalNaam(r.kanaal) },
  { header: 'Medewerker', get: (r) => r.user_name || '' },
  { header: 'Uitkomst', get: (r) => (AI_UITKOMST[r.uitkomst] || {}).label || '' },
  { header: 'Beoordeling', get: (r) => aiBeoordelingNaam(r.beoordeling) },
  { header: 'Opmerking', get: (r) => r.opmerking || '' },
  { header: 'Geraadpleegde klantgegevens', get: (r) => (r.geraadpleegd || []).map((g) => aiHoofdletter(g.label)).join(', ') },
  { header: 'Duur (s)', get: (r) => (isGetal(r.duur_ms) ? Math.round(Number(r.duur_ms) / 100) / 10 : '') },
  { header: 'Tokens in', get: (r) => r.input_tokens ?? '' },
  { header: 'Tokens uit', get: (r) => r.output_tokens ?? '' },
  { header: 'Model', get: (r) => r.model || '' },
  { header: 'Telt mee voor het leren', get: (r) => (r.leren_uit ? 'nee' : 'ja') },
];
